/**
 * `Reconcile` — `mockups/studio/money.html`'s topbar, the primary button.
 *
 * `payments_backfill$` pulls the Wise statement now rather than waiting for tonight's
 * `wise.backfill` cron. Every credit is deduped on `step_id`, so overlapping a webhook
 * that has already arrived costs nothing and running it twice in a minute is a no-op —
 * which is why the confirmation is short.
 *
 * It is a `$` action and so it confirms, but there is no amount to name: the call is
 * "go and look", and what it finds is what it finds. The button says what it will do
 * and over what window, which is the honest version of naming the figure.
 *
 * **And it says what happened afterwards.** Session 24 (R2-32) found the dialog simply
 * closing: no line, no count, no visible change, and no way to tell whether the button
 * had done anything at all. The thing that made that hard to fix well is real — the
 * pull is a network call to Wise and therefore a job, so the request cannot answer
 * "how many did it find" — but "we cannot tell you the one number" is not a reason to
 * tell somebody nothing. So the action was given an answer worth printing (`waiting`,
 * and a `queued` that is `false` when a pull was already in flight), the dialog stays
 * open on success, and it says which of those two happened and what the queue holds to
 * compare against a moment later.
 */

import { useState } from 'react'

import { formatList, type Money } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction } from '../../../api/studio.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { Button } from '../../../components/portal/Button.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Prose } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'

/** How far back a hand-run reconcile looks. The job's own daily window is 26. */
export const RECONCILE_HOURS = 72

/** What `payments_backfill$` answers with, once it is not a `needs_review`. */
type BackfillResult = {
  readonly queued: boolean
  readonly hours: number
  readonly waiting: { readonly count: number; readonly totals: readonly Money[] }
}

export function ReconcileButton() {
  const [open, setOpen] = useState(false)
  const backfill = useStudioAction('payments_backfill')
  const review = reviewOf(backfill.data)
  const result = review === null ? resultOf(backfill.data) : null

  return (
    <>
      <Button
        size="sm"
        variant="primary"
        onClick={() => {
          setOpen(true)
        }}
      >
        Reconcile
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          /* A second opening is a second question, not the first one's answer again. */
          if (!next) backfill.reset()
        }}
        title="Pull the Wise statement now"
        confirmLabel={
          result === null
            ? `Pull the last ${String(RECONCILE_HOURS)} hours of Wise credits`
            : 'Pull them again'
        }
        /*
         * `Done`, not `Close`: session 24 gave every dialog a `Close` in its head, so
         * a second control saying the same word is two buttons with one name — which
         * is how a screen reader reads it, and how `getByRole` read it. `Done`
         * acknowledges the answer above it; the head's `Close` is the way out of any
         * dialog, answered or not.
         */
        cancelLabel={result === null ? 'Cancel' : 'Done'}
        busy={backfill.isPending}
        onConfirm={() => {
          backfill.mutate({ hours: RECONCILE_HOURS })
        }}
      >
        <Stack>
          <Prose>
            {`This reads the Wise statement for the last ${String(RECONCILE_HOURS)} hours and matches what it can. Credits are deduped on their step id, so anything a webhook already delivered is skipped rather than counted twice — running this again in a minute changes nothing.`}
          </Prose>
          <Prose>
            What it cannot place lands in the review queue below, where a person decides. Nothing is
            charged to anybody and nothing is sent.
          </Prose>
          {review === null ? null : <NeedsReviewNotice review={review} />}
          {result === null ? null : <Outcome result={result} />}
          {backfill.isError ? (
            <Notice need role="alert">
              {messageOf(backfill.error)}
            </Notice>
          ) : null}
        </Stack>
      </ConfirmDialog>
    </>
  )
}

/**
 * What the call did, in one strip.
 *
 * Two sentences, and the second is the one the report asked for: "0 new credits, 4
 * still waiting" is not answerable — the pull has not run yet — but "4 are waiting
 * now, and what it finds joins them" is, and it gives the reader the figure to compare
 * the queue below against once it has.
 *
 * `queued: false` reads as success and is not: a pull was already in flight, so this
 * click added nothing. It is not a failure either — the one running covers the same
 * window — so it is a plain strip and not an ochre one.
 */
function Outcome({ result }: { readonly result: BackfillResult }) {
  const format = useFormat()
  const { count, totals } = result.waiting
  const money = formatList(totals.map((one) => format.money(one)))

  return (
    <Notice role="status">
      {result.queued
        ? `Queued. The last ${String(result.hours)} hours of Wise credits are being pulled now; it takes a few seconds. `
        : `Not queued — a pull was already running, and it covers the same window, so nothing was lost or duplicated. `}
      {count === 0
        ? 'Nothing is waiting to be placed at the moment, so anything this finds will be the whole of the queue below.'
        : `${format.qty(count, { one: 'credit', other: 'credits' })} — ${money} — are waiting to be placed in the queue below, and whatever this cannot match joins them.`}
    </Notice>
  )
}

/**
 * The backfill's own answer, or null for a `needs_review` or nothing yet.
 *
 * The same narrowing `DryRunDialog` does for `dry_run`: `useStudioAction` types `data`
 * as the action's output **or** a `needs_review` result, so the field is looked for
 * rather than read.
 */
function resultOf(data: unknown): BackfillResult | null {
  if (typeof data !== 'object' || data === null || !('waiting' in data)) return null
  return data as BackfillResult
}
